/**
 * The course a plan is for: one state, one grade, one subject (R3). Pure.
 *
 * The values are the Learning Commons Knowledge Graph's own `jurisdiction`,
 * `academicSubject`, and `gradeLevel` filters, copied from Objectives' profile, so a value
 * the API would reject never reaches it.
 */

export const STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'Washington, D.C.', 'Multi-State',
] as const;

export const SUBJECTS = ['English Language Arts', 'Mathematics', 'Science', 'Social Studies'] as const;

export const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'] as const;

export type State = (typeof STATES)[number];
export type Subject = (typeof SUBJECTS)[number];
export type GradeLevel = (typeof GRADE_LEVELS)[number];

/** Each subject's short form for a bar or a heading (KTD6). */
const SUBJECT_SHORT: Record<Subject, string> = {
  'English Language Arts': 'ELA',
  Mathematics: 'Math',
  Science: 'Science',
  'Social Studies': 'Soc. St.',
};

/** The subject's short form and the grade, as "Math 3" (KTD6). */
export const shortLabel = (course: Course): string => `${SUBJECT_SHORT[course.subject]} ${course.grade}`;

/**
 * Each course's short label, with the state appended to any label another course shares, and
 * " #2", " #3" by input order to any that still clash (same subject, grade, and state).
 */
export function shortLabels(courses: readonly Course[]): string[] {
  const clashing = (labels: string[]) => labels.map(label => labels.indexOf(label) !== labels.lastIndexOf(label));
  const short = courses.map(shortLabel);
  const withState = clashing(short).map((clash, i) => (clash ? `${short[i]} (${courses[i]!.state})` : short[i]!));
  const seen = new Map<string, number>();
  return withState.map(label => {
    const n = (seen.get(label) ?? 0) + 1;
    seen.set(label, n);
    return n > 1 ? `${label} #${n}` : label;
  });
}

export interface Course {
  state: State;
  grade: GradeLevel;
  subject: Subject;
}

/** One official standard in the course, as the plan and the model see it. */
export interface CourseStandard {
  id: string;
  code: string;
  statement: string;
}

/**
 * A standard's code and statement by id. A standard with no stored text reads as its id, so
 * it is still named and can still be moved.
 */
export function standardLookup(standards: ReadonlyArray<CourseStandard>): (id: string) => CourseStandard {
  const text = new Map(standards.map(s => [s.id, s]));
  return id => text.get(id) ?? { id, code: '', statement: id };
}

/** How a standard is named in short: its code, or its statement when it has none. */
export const standardLabel = (s: Pick<CourseStandard, 'code' | 'statement'>): string => s.code || s.statement;

export interface CourseStandardSet {
  standards: CourseStandard[];
  /** True when the set is a 9-12 grade band rather than one grade's course. */
  band: boolean;
  provider: 'learning-commons' | 'fixture';
}

const HIGH_SCHOOL: readonly GradeLevel[] = ['9', '10', '11', '12'];

export const isHighSchool = (grade: GradeLevel): boolean => HIGH_SCHOOL.includes(grade);

/**
 * Draft my year is K-8 only. High-school standards are published by band, not by course,
 * so a 9-12 draft would spread several courses across one year.
 */
export const draftAllowed = (grade: GradeLevel): boolean => !isHighSchool(grade);

/** The grade levels to list: the grade itself, or the whole band for high school (R8). */
export const gradesToLoad = (grade: GradeLevel): GradeLevel[] => (isHighSchool(grade) ? [...HIGH_SCHOOL] : [grade]);

const isOneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (values as readonly string[]).includes(value);

/** A course from untrusted input, or null when any field is not one the API accepts. */
export function parseCourse(value: unknown): Course | null {
  if (!value || typeof value !== 'object') return null;
  const { state, grade, subject } = value as Record<string, unknown>;
  return isOneOf(STATES, state) && isOneOf(GRADE_LEVELS, grade) && isOneOf(SUBJECTS, subject) ? { state, grade, subject } : null;
}
